import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import api from '../api/axiosConfig'
import { useLanguage } from '../i18n/LanguageContext';
import './TagInput.css';
import { Tag } from '../types/authTypes';

interface TagInputProps {
  tags: Tag[];
  setTags: (tags: Tag[]) => void;
}

export interface TagInputRef {
  refreshRecentTags: () => Promise<void>;
}

// Background -> text colour. Every pair clears 4.5:1, and the text is a
// tint of its background rather than flat black or white. Regenerate with
// the same rule if colours are added: light tag, dark text; dark tag,
// light text; never a near-match of the two.
const colorPairs: { [key: string]: string } = {
  "#5b94f1ff": "#172c4f",
  "#ea6e6eff": "#531818",
  "#5becbbff": "#15524a",
  "#efbc65ff": "#4a3510",
  "#b497f6ff": "#2e1d5c",
  "#7de9f9ff": "#08404a",
  "#f4bdd9ff": "#5a2440",
  "#f99a7aff": "#5a2918",
  "#f7e499ff": "#4f3e05",
  "#f797f1ff": "#5a0e4e",
  "#8160cfff": "#fcfbfe",
  "#06b6d4ff": "#0a3d47",
  "#10b981ff": "#123f30",
  "#3b82f6ff": "#0f1d33",
  "#45b7d1ff": "#16414b",
  "#4ecdc4ff": "#1a4944",
  "#84cc16ff": "#2d4a08",
  "#8b5cf6ff": "#120a24",
  "#96ceb4ff": "#2d4a3b",
  "#98d8c8ff": "#2a4a42",
  "#98d8e8ff": "#2a4a52",
  "#dda0ddff": "#5c2d5c",
  "#ec4899ff": "#3b1126",
  "#f59e0bff": "#5c3a08",
  "#f7dc6fff": "#5c4f1a",
  "#ff6b6bff": "#5c1f1f",
  "#ffeaa7ff": "#5c4d1a"
};

// Ten keeps the grid to a single row at the modal's width. Twelve spilled a
// second row that ate the gap between the tags and the bottom of the modal.
const RECENT_TAG_COUNT = 10;

/** Relative luminance, per WCAG. */
const luminance = (hex: string): number => {
  const clean = hex.replace('#', '').slice(0, 6);
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
  const channel = (pair: string) => {
    const c = parseInt(pair, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(full.slice(0, 2))
    + 0.7152 * channel(full.slice(2, 4))
    + 0.0722 * channel(full.slice(4, 6));
};

/**
 * Readable text for a tag background.
 *
 * The curated pairs come first because they are tinted to their background.
 * Anything outside the palette — a colour picked by hand, or one added later —
 * falls back to luminance: a light tag takes dark text, a dark tag takes light.
 */
export const textColorFor = (background: string): string => {
  const known = colorPairs[background] || colorPairs[background?.toLowerCase()];
  if (known) return known;
  return luminance(background) > 0.35 ? '#1a1a1a' : '#f0f0f0';
};

const TagInput = forwardRef<TagInputRef, TagInputProps>(({ tags, setTags }, ref) => {
  const { t } = useLanguage();
  const colors = Object.keys(colorPairs);
  const textColors = ['#ffffff', '#f0f0f0', '#dddddd', '#cccccc', '#999999',
                      '#666666', '#333333', '#1a1a1a', '#000000', '#ffd700',
                      '#ff6b6b', '#4ecdc4', '#6f4e37', '#7b3fa0', '#2f7d52'];
  const [input, setInput] = useState<string>('');
  const [suggestions, setSuggestions] = useState<Tag[]>([]);
  const [recentTags, setRecentTags] = useState<Tag[]>([]);
  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [editingType, setEditingType] = useState<'background' | 'text' | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState('below');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const handleEditColor = (tagId: number, type: 'background' | 'text', event: React.MouseEvent<HTMLButtonElement>): void => {
    if (editingTagId === tagId && editingType === type) {
      setEditingTagId(null);
      setEditingType(null);
    } else {
      setEditingTagId(tagId);
      setEditingType(type);
    }
    
    // Simple viewport detection - you can make this more sophisticated
    const tagElement = event.currentTarget.closest('.selected-tag');
    const rect = tagElement?.getBoundingClientRect();
    
    // If tag is in bottom half of viewport, show dropdown above
    if (rect && rect.bottom > window.innerHeight / 2) {
      setDropdownPosition('above');
    } else {
      setDropdownPosition('below');
    }
  };

  // Color history management
  const getColorHistory = (): string[] => {
    const history = localStorage.getItem('tagColorHistory');
    return history ? JSON.parse(history) : [];
  };

  const updateColorHistory = (color: string): void => {
    const history = getColorHistory();
    const updatedHistory = [color, ...history.filter(c => c !== color)];
    // Keep history length equal to available colors to avoid repeats
    const maxHistory = colors.length;
    localStorage.setItem('tagColorHistory', JSON.stringify(updatedHistory.slice(0, maxHistory)));
  };

  const getSmartColor = (): string => {
    const usedColors = (tags || []).map(existing => existing.color);
    const colorHistory = getColorHistory();
    
    // First, try colors not used in current tags
    const availableColors = colors.filter(c => !usedColors.includes(c));
    
    if (availableColors.length > 0) {
      // From available colors, avoid recently used ones
      const recentlyUsed = colorHistory.slice(0, Math.floor(colors.length / 2));
      const freshColors = availableColors.filter(c => !recentlyUsed.includes(c));
      
      if (freshColors.length > 0) {
        return freshColors[Math.floor(Math.random() * freshColors.length)];
      }
      
      // If all available colors are recent, pick the least recent
      return availableColors[Math.floor(Math.random() * availableColors.length)];
    }
    
    // If all colors are used, pick the least recently used
    const leastRecentColors = colors.filter(c => !colorHistory.slice(0, 3).includes(c));
    if (leastRecentColors.length > 0) {
      return leastRecentColors[Math.floor(Math.random() * leastRecentColors.length)];
    }
    
    // Fallback to random from palette
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const normalizeTagName = (name: string): string => {
    return name.toLowerCase().trim().replace(/\s+/g, '-');
  };

  useEffect(() => {
    api.get('/tags/recent/')
      .then(res => {
        setRecentTags((res.data || []).slice(0, RECENT_TAG_COUNT));
      })
      .catch(err => {
        console.error('TagInput: Error fetching tags:', err);
        setRecentTags([]);
      });
  }, []);

  useEffect(() => {
    if (suggestions.length > 0 && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [suggestions]);

  useEffect(() => {
    if (input.length > 1) {
      api.get(`/tags/search/?q=${input}`)
        .then(res => {
          setSuggestions(res.data)
        })
        .catch(err => {
          console.error('TagInput: Search error:', err);
        });
    } else {
      setSuggestions([]);
    }
  }, [input]);

  useEffect(() => {
    // The TagInput component should use the tags prop directly instead of maintaining separate state
    // If you have internal tag state, update it here:
    // setInternalTags(tags); // if you have internal state
  }, [tags]);

  const handleAddTag = (tag: Tag): void => {
    const currentTags = tags || [];
    if (!currentTags.some(existing => existing.id === tag.id && existing.name === tag.name)) {
      // Ensure the tag name is normalized
      const normalizedTag = { ...tag, name: normalizeTagName(tag.name) };
      updateColorHistory(normalizedTag.color);
      setTags([...currentTags, normalizedTag]); // Use currentTags
      setInput('');
      setSuggestions([]);
    }
  };

  const handleCreateTag = async (): Promise<void> => {
    const normalizedName = normalizeTagName(input);
    const currentTags = tags || [];
    if (normalizedName && !currentTags.some(existing => existing.name === normalizedName)) {
      // Typing the name of a tag you already own must reuse that tag, not
      // mint a temporary one. A temporary id skips the colour PATCH, and the
      // note save then hits get_or_create, finds the real tag and drops the
      // colour — the edit disappears with no error anywhere.
      try {
        const { data } = await api.get(`/tags/search/?q=${encodeURIComponent(normalizedName)}`);
        const existing = (data || []).find((candidate: Tag) => candidate.name === normalizedName);
        if (existing) {
          handleAddTag(existing);
          return;
        }
      } catch (err) {
        // Offline or the search failed: fall through and create it locally
        console.error('TagInput: could not check for an existing tag:', err);
      }

      const backgroundColor = getSmartColor();
      
      // Get the corresponding text color, fallback to white
      const textColor = textColorFor(backgroundColor);
      
      updateColorHistory(backgroundColor);
      const newTag = { 
        name: normalizedName, 
        color: backgroundColor, 
        text_color: textColor,
        id: Date.now() 
      };
      setTags([...currentTags, newTag]);
      setInput('');
      await refreshRecentTags();
    }
  };

  const refreshRecentTags = async () => {
    try {
      const response = await api.get('/tags/recent/');
      setRecentTags((response.data || []).slice(0, RECENT_TAG_COUNT));
    } catch (err) {
      console.error('TagInput: Error fetching recent tags:', err);
    }
  };

  useImperativeHandle(ref, () => ({
    refreshRecentTags
  }));

  const handleColorChange = async (tagId: number, newColor: string): Promise<void> => {
    // Update the local tags array
    const currentTags = tags || [];
    const updatedTags = currentTags.map(tag => 
      tag.id === tagId ? { ...tag, color: newColor } : tag
    );
    setTags(updatedTags);
    updateColorHistory(newColor);
    
    // Update the tag in the backend if it has a real ID (not a Date.now() temporary ID)
    if (tagId < 999999999999) { // Real database IDs are typically smaller
      try {
        await api.patch(`/tags/${tagId}/`, 
          { color: newColor },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
      } catch (error) {
        console.error('Failed to update tag color:', error);
      }
    }
    
    setEditingTagId(null);
    setEditingType(null);
    await refreshRecentTags();
  };

  const handleTextColorChange = async (tagId: number, newTextColor: string): Promise<void> => {
    // Update the local tags array
    const currentTags = tags || [];
    const updatedTags = currentTags.map(tag => 
      tag.id === tagId ? { ...tag, text_color: newTextColor } : tag
    );
    setTags(updatedTags);
    
    // Update the tag in the backend if it has a real ID (not a Date.now() temporary ID)
    if (tagId < 999999999999) { // Real database IDs are typically smaller
      try {
        await api.patch(`/tags/${tagId}/`, 
          { text_color: newTextColor },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
      } catch (error) {
        console.error('Failed to update tag text color:', error);
      }
    }
    
    setEditingTagId(null);
    setEditingType(null);
    await refreshRecentTags();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const exactMatch = suggestions.find(tag => tag.name === normalizeTagName(input));

      if (exactMatch) {
        handleAddTag(exactMatch);
      } else {
        handleCreateTag();
      }
    }
  };

  const removeTag = (id: number): void => {
    const currentTags = tags || [];
    setTags(currentTags.filter(tag => tag.id !== id));
  };

  return (
    <>
      <div className="tag-input-container">
        {/* The chosen tags live inside the field, the way recipients do in a
            mail client: what is on the note and how to add more are one
            control, and there is no separate list to mistake for the other. */}
        <div className="tag-label">
          {t.tagInput.label} <span className="tag-label-req">{t.tagInput.required}</span>
        </div>

        <div className="input-dropdown-wrapper">
          <div
            className="tag-combo"
            onClick={() => inputRef.current?.focus()}
          >
            <div className="selected-tags">
              {Array.isArray(tags) ? tags.map(tag => (
                <span 
                  key={tag.id} 
                  className="selected-tag" 
                  style={{ backgroundColor: tag.color, color: tag.text_color }}
                >
                  {tag.name}
                  <button 
                    type="button"
                    onClick={(e) => handleEditColor(tag.id, 'background', e)}
                    className="edit-color-btn"
                    title={t.tagInput.editBackgroundColor}
                  >🎨</button>
                  <button 
                    type="button"
                    onClick={(e) => handleEditColor(tag.id, 'text', e)}
                    className="edit-text-color-btn"
                    title={t.tagInput.editTextColor}
                  >A</button>
                  <button 
                    type="button"
                    onClick={() => removeTag(tag.id)}
                    className="remove-tag-btn"
                    aria-label={t.tagInput.removeTag}
                  >×</button>

                  {editingTagId === tag.id && editingType === 'background' && (
                    <div className={`color-picker-dropdown ${dropdownPosition}`}>
                      {Array.isArray(colors) ? colors.map(color => (
                        <div
                          key={color}
                          className="color-option"
                          style={{ backgroundColor: color }}
                          onClick={() => handleColorChange(tag.id, color)}
                        />
                      )) : null}
                    </div>
                  )}

                  {editingTagId === tag.id && editingType === 'text' && (
                    <div className={`color-picker-dropdown ${dropdownPosition}`}>
                      {textColors.map(color => (
                        <div
                          key={color}
                          className="color-option"
                          style={{ 
                            backgroundColor: color,
                            border: color === '#ffffff' || color === '#f0f0f0' || color === '#dddddd' ? '2px solid #666' : 'none'
                          }}
                          onClick={() => handleTextColorChange(tag.id, color)}
                        />
                      ))}
                    </div>
                  )}
                </span>
              )) : null}
            </div>

            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value.toLowerCase())}
              onKeyDown={handleKeyPress}
              placeholder={tags.length ? t.tagInput.addAnother : t.tagInput.addTags}
              className="tag-input-field"
            />
          </div>

          { Array.isArray(suggestions) && suggestions.length > 0 && (
            <div className="suggestions-dropdown" style={dropdownStyle}>
              { suggestions.map(tag => (
                <div
                  key={tag.id}
                  className="suggestion-item"
                  onClick={() => handleAddTag(tag)}
                  style={{ borderLeft: `4px solid ${tag.color}` }}
                >
                  {tag.name}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="tag-label recent-label">{t.tagInput.recentClickToAdd}</div>
        <div className="recent-tags-grid">
          { Array.isArray(recentTags) ? recentTags.map(tag => (
            <button
              type="button"
              key={tag.id}
              className="recent-tag-item"
              onClick={() => handleAddTag(tag)}
              style={{ backgroundColor: tag.color, color: tag.text_color }}
            >
              {tag.name}
            </button>
          )) : null}
        </div>


      </div>
    </>
  );
});

export default TagInput;